// Primary marketing header navigation (LANDING_INSPIRATION G3):
// Product · Use cases · Resources · Services · Pricing, left-aligned next to the logo,
// auth actions right. Kept tiny + declarative so the header stays
// presentational. A link with `items` renders a hover popover; `kind` selects
// a designed popover body (the Product card-in-card grid / the shared group-
// column panel Use cases and Resources render) while `items` doubles as the
// flat mobile-drawer list for the same link. The old Connect popover is
// dissolved: integrations live in the Product popover, "Switching from" in
// Resources.
//
// A link can also be scoped to one surface with `only`, so the same label can
// ship two shapes: Use cases is a designed panel on desktop and a single
// tappable row in the drawer. Plain (item-less) rows carry an `icon` the
// drawer boxes like a sub-item; the desktop nav ignores it.

import type { ComponentType } from 'react';
import {
	ArrowLeftRight,
	BookOpen,
	Brain,
	Briefcase,
	Compass,
	FlaskConical,
	Hammer,
	Handshake,
	Monitor,
	Newspaper,
	ShieldCheck,
	Sparkles,
	Tag,
	Users,
	Waypoints,
} from 'lucide-react';

import { PRODUCT_FEATURE_LINKS, productHref } from '../content/product/product-links';
import { BOOK_DEMO_HREF, BOOK_DEMO_LABEL } from './book-demo';

export interface MarketingNavSubItem {
	label: string;
	href: string;
	description?: string;
	/** Boxed icon shown left of the label in list popovers + the mobile drawer. */
	icon?: ComponentType<{ className?: string }>;
	/** Optional group label; consecutive items sharing one render under a small uppercase header. */
	group?: string;
}

/** Which nav surface a link shows on. Omitted = both desktop and mobile. */
export type MarketingNavPlatform = 'desktop' | 'mobile';

/** The footer row under a designed popover body: quiet note left, one colored link right. */
export interface MarketingNavPanelFooter {
	note: string;
	linkLabel: string;
	linkHref: string;
}

export interface MarketingNavLink {
	label: string;
	href?: string;
	items?: readonly MarketingNavSubItem[];
	/** Boxed icon for an item-less row in the mobile drawer; desktop ignores it. */
	icon?: ComponentType<{ className?: string }>;
	/**
	 * Designed desktop popover body. Every designed panel shares the Product
	 * anatomy (card-in-card, grey label + white benefit cells, hairline column
	 * rules, footer row): 'product' derives its cells from PRODUCT_STAGES,
	 * 'columns' lays `items` out column-major by `group`. Omitted = the default
	 * icon-row list. The mobile drawer always renders `items` flat.
	 */
	kind?: 'product' | 'columns';
	/** Footer row for a 'columns' panel ('product' derives its own from release notes). */
	panelFooter?: MarketingNavPanelFooter;
	/**
	 * Explicit column count for a 'columns' panel. Flips the layout row-major
	 * (items fill left-to-right), so each `group` reads as a row instead of a
	 * column. Omitted = one column per `group`.
	 */
	panelColumns?: number;
	/** Restrict this link to a single nav surface. Omitted = shown on both. */
	only?: MarketingNavPlatform;
}

// Mobile-drawer rows for the Product group: the five stages + Integrations
// (icon rows, derived from the same spine registry as the desktop grid).
const PRODUCT_ITEMS: readonly MarketingNavSubItem[] = PRODUCT_FEATURE_LINKS.map((link) => ({
	label: link.label,
	href: productHref(link),
	description: link.description,
	icon: link.icon,
}));

// The Resources popover (G3, Linear's benefit-line inversion): audience
// moments - company-curious / evaluator / buyer-in-motion - expressed as
// `group`s, laid row-major into the 3x2 grid. Every benefit line states only
// what its target page actually claims (e.g. /security names EU hosting +
// every sub-processor).
export const RESOURCES_ITEMS: readonly MarketingNavSubItem[] = [
	{
		label: 'About',
		href: '/about',
		description: 'Who builds Naumu',
		icon: Users,
		group: 'Company',
	},
	{
		label: 'Blog',
		href: '/blog',
		description: 'How we build',
		icon: Newspaper,
		group: 'Company',
	},
	{
		label: 'Docs',
		href: '/docs',
		description: 'How to use Naumu',
		icon: BookOpen,
		group: 'Evaluate',
	},
	{
		label: 'Security',
		href: '/security',
		description: 'EU hosting, every subprocessor named',
		icon: ShieldCheck,
		group: 'Evaluate',
	},
	{
		label: 'Switching from',
		href: '/compare',
		description: 'Side-by-side with Slack, Notion, and Jira',
		icon: ArrowLeftRight,
		group: 'Switch',
	},
	{
		label: 'Desktop app',
		href: '/product/desktop',
		description: 'Naumu on Mac, Windows, and Linux',
		icon: Monitor,
		group: 'Switch',
	},
];

export const NAV_LINKS: readonly MarketingNavLink[] = [
	{
		// The trigger navigates to the hub; hovering opens the stage grid.
		label: 'Product',
		href: '/product',
		kind: 'product',
		items: PRODUCT_ITEMS,
	},
	{
		// Desktop only: the drawer would render these six as a second Product-sized
		// section, so mobile gets the single row below instead.
		label: 'Use cases',
		href: '/use-cases',
		only: 'desktop',
		kind: 'columns',
		// 3x2 row-major: the "For companies" trio is the top row, the
		// "For individuals" trio the bottom - full three-column panel width.
		panelColumns: 3,
		panelFooter: {
			note: 'One memory, every team.',
			linkLabel: 'All use cases',
			linkHref: '/use-cases',
		},
		items: [
			{
				label: 'For teams',
				href: '/use-cases/teams',
				description: "Your company's working memory, in one place.",
				icon: Users,
				group: 'For companies',
			},
			{
				label: 'Sales & client teams',
				href: '/use-cases/sales-teams',
				description: 'Every deal, promise, and follow-up - queryable.',
				icon: Handshake,
				group: 'For companies',
			},
			{
				label: 'Agencies & consultancies',
				href: '/use-cases/agencies',
				description: "Every client's history, ready when they call.",
				icon: Briefcase,
				group: 'For companies',
			},
			{
				label: 'For researchers',
				href: '/use-cases/researcher',
				description: 'A connected map with a source on every fact.',
				icon: FlaskConical,
				group: 'For individuals',
			},
			{
				label: 'For solo builders',
				href: '/use-cases/solo-builder',
				description: 'You ship faster than you can file.',
				icon: Hammer,
				group: 'For individuals',
			},
			{
				label: 'Personal knowledge base',
				href: '/use-cases/personal-kb',
				description: 'Your own notes, findable years later.',
				icon: Brain,
				group: 'For individuals',
			},
		],
	},
	{ label: 'Use cases', href: '/use-cases', only: 'mobile', icon: Compass },
	{
		// Popover-only - no `href`, so the trigger never navigates on its own
		// (see MarketingNavItem: no href renders a <button>, not a <Link>).
		label: 'Resources',
		kind: 'columns',
		// 3x2 row-major: with uneven group sizes (2/2/2) group-per-column leaves
		// holes in the grid - six items laid left-to-right fill it exactly. Agent
		// skills was dropped from the popover (user ruling, Aug 2026) to make room
		// for About; it stays reachable from the footer and /docs.
		panelColumns: 3,
		panelFooter: {
			note: 'Prefer a walkthrough?',
			linkLabel: BOOK_DEMO_LABEL,
			linkHref: BOOK_DEMO_HREF,
		},
		items: RESOURCES_ITEMS,
	},
	// The AI-native transformation offer (/services): a plain link, not a
	// Resources cell - it is a thing we sell, and the Resources 3x2 grid is full.
	{ label: 'Services', href: '/services', icon: Waypoints },
	{ label: 'Pricing', href: '/pricing', icon: Tag },
] as const;

// Per-surface views of NAV_LINKS. The registry stays the single source of truth;
// each surface filters by the link's `only` flag (omitted = shown on both).
export const DESKTOP_NAV_LINKS: readonly MarketingNavLink[] = NAV_LINKS.filter(
	(link) => link.only !== 'mobile',
);
export const MOBILE_NAV_LINKS: readonly MarketingNavLink[] = NAV_LINKS.filter(
	(link) => link.only !== 'desktop',
);
